/**
 * R-14 T-087 / T-068 — the shell's experience, mounted once around the shell as the kit's
 * DisclosureProvider: the user's tier for the app on screen (their choice, else the instance default
 * for their role — the API answers both) and each element's minimum (the registry row, else the
 * module declaration's, else Expert); plus the app's home for the user's scope. Kit components and
 * the shell read the tier with `useDisclosure(elementId)`; the user menu switches it.
 */
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { appForLocation } from '@/app/platform';
import { appForPath, useApps } from '@/features/apps/apps-api';
import { DisclosureProvider, type DisclosureValue } from '@/kit';
import { type AppHomeAnswer, needsFreshRead, useAppHome, useDisclosureRows, useExperiencePreference, useSetExperience } from './experience-api';
import { BEGINNER, declaredTiers, EXPERT, minTierFor, resolveTier, type Tier } from './tiers';

interface ExperienceValue {
  /** the app the tier and home belong to (the app on screen, else the first published app) */
  app: string;
  tier: Tier;
  /** the preference has answered (or failed): the tier is the user's, not the Expert placeholder */
  ready: boolean;
  /** `instance_default` until the person chooses */
  source: 'user' | 'instance_default' | null;
  home: AppHomeAnswer | null;
  setTier: (tier: Tier) => Promise<void>;
  saving: boolean;
}

const Ctx = createContext<ExperienceValue>({ app: 'cigs', tier: EXPERT, ready: true, source: null, home: null, setTier: async () => {}, saving: false });

export function ExperienceProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const { pathname, search } = useLocation();
  const apps = useApps(enabled);
  const list = apps.data?.apps ?? [];
  const app = appForLocation(list, pathname, search, appForPath)?.slug ?? list[0]?.slug ?? 'cigs';
  const ready = enabled && apps.isFetched;
  const pref = useExperiencePreference(app, ready);
  // R-15 #882: entering an app (the portal card, a route into an app's pages) reads the tier afresh when the
  // cached answer is older than the entry age or failed; inside one app session the cached answer stands.
  const entered = appForLocation(list, pathname, search, appForPath)?.slug ?? null;
  const lastEntered = useRef<string | null>(null);
  const { refetch, dataUpdatedAt, isError, isFetching, isFetched } = pref;
  useEffect(() => {
    if (!ready || entered === lastEntered.current) return;
    lastEntered.current = entered;
    // a read already on its way (the first one, or one the app change started) is the fresh read
    if (entered && isFetched && !isFetching && needsFreshRead({ dataUpdatedAt, isError })) void refetch();
  }, [ready, entered, dataUpdatedAt, isError, isFetching, isFetched, refetch]);
  // the tier is known once the person's profile, the apps and the preference have answered (or failed)
  const known = ready && pref.isFetched;
  const rows = useDisclosureRows(app, ready);
  const home = useAppHome(app, ready);
  const save = useSetExperience(app);
  const { mutateAsync } = save;
  const setTier = useCallback(async (tier: Tier) => void (await mutateAsync(tier)), [mutateAsync]);
  // Until the preference answers, the shell shows the simple set, never Expert: an Expert placeholder
  // flashed the full menus at a department user and showed the Expert mode switch as on, so a switch
  // read in that window was taken as already on and the shell then fell back to Beginner (golden 3).
  // A failed read is Expert (resolveTier, PR #78); an answered read carries the app's default (CIGS: Beginner).
  const tier = known ? resolveTier(pref.data?.tier) : BEGINNER;
  const disclosure = useMemo<DisclosureValue>(() => {
    const declared = declaredTiers(apps.data);
    const all = rows.data ?? [];
    return { tier, minTier: (id) => minTierFor(all, id, declared.get(id), app) };
  }, [tier, rows.data, apps.data, app]);
  const value = useMemo<ExperienceValue>(
    () => ({ app, tier, ready: known, source: pref.data?.source ?? null, home: home.data ?? null, setTier, saving: save.isPending }),
    [app, tier, known, pref.data?.source, home.data, setTier, save.isPending],
  );
  return (
    <Ctx.Provider value={value}>
      <DisclosureProvider value={disclosure}>{children}</DisclosureProvider>
    </Ctx.Provider>
  );
}

export function useExperience(): ExperienceValue {
  return useContext(Ctx);
}
